/**
 * Validation Expression Guide Modal
 * Shows help and examples for writing validation expressions
 */

import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  Chip,
  Stack,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { BglSecondarySmallButton } from 'components/button/BglButtons';

interface ValidationExpressionGuideModalProps {
  open: boolean;
  onClose: () => void;
}

export const ValidationExpressionGuideModal: React.FC<ValidationExpressionGuideModalProps> = ({
  open,
  onClose,
}) => {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h6">Validation Expression Guide</Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={3}>
          {/* Available Variables */}
          <Box>
            <Typography variant="subtitle2" fontWeight={600} color="text.primary" sx={{ mb: 1 }}>
              Available Variables
            </Typography>
            <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mb: 1 }}>
              <Chip label="value" size="small" variant="outlined" />
              <Chip label="configKey" size="small" variant="outlined" />
              <Chip label="dataType" size="small" variant="outlined" />
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
              Note: Cannot use var/let/const. Use the predefined variables above.
            </Typography>
          </Box>

          {/* Available Functions */}
          <Box>
            <Typography variant="subtitle2" fontWeight={600} color="text.primary" sx={{ mb: 1 }}>
              Available Functions
            </Typography>
            <Stack direction="row" spacing={0.5} flexWrap="wrap">
              <Chip label="inArray(val, arr)" size="small" variant="outlined" />
              <Chip label="matches(val, pattern)" size="small" variant="outlined" />
              <Chip label="length(val)" size="small" variant="outlined" />
            </Stack>
          </Box>

          {/* Examples */}
          <Box>
            <Typography variant="subtitle2" fontWeight={600} color="text.primary" sx={{ mb: 1 }}>
              Examples
            </Typography>
            <Box component="pre" sx={{ fontFamily: 'monospace', fontSize: '0.875rem', bgcolor: 'grey.100', p: 2, borderRadius: 1, overflow: 'auto' }}>
{`// Simple range check
value > 0 && value < 100

// Using predefined variables
value > 10 || dataType == "INTEGER"

// String length validation
length(value) >= 5 && length(value) <= 50

// Array validation with custom function
inArray(value, ["option1", "option2", "option3"])

// Regex pattern matching
matches(value, "^[A-Z]{3}-[0-9]{4}$")

// Complex conditional
value > 0 ? value < 100 : true

// With custom error message (JSON format)
{
  "script": "length(value) >= 5",
  "errorMessage": "Must be at least 5 characters"
}`}
            </Box>
          </Box>

          {/* Supported Operators */}
          <Box>
            <Typography variant="subtitle2" fontWeight={600} color="text.primary" sx={{ mb: 1 }}>
              Supported Operators
            </Typography>
            <Typography variant="body2" color="text.secondary" component="div">
              • <strong>Logical:</strong> <code>&&</code>, <code>||</code>, <code>!</code><br />
              • <strong>Comparison:</strong> <code>&gt;</code>, <code>&lt;</code>, <code>&gt;=</code>, <code>&lt;=</code>, <code>==</code>, <code>!=</code><br />
              • <strong>Arithmetic:</strong> <code>+</code>, <code>-</code>, <code>*</code>, <code>/</code>, <code>%</code><br />
              • <strong>Ternary:</strong> <code>condition ? true_value : false_value</code>
            </Typography>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <BglSecondarySmallButton onClick={onClose}>
          Close
        </BglSecondarySmallButton>
      </DialogActions>
    </Dialog>
  );
};

